<template>
	<div class="flex gap-2">
		<app-button
			v-if="product.id"
			icon="fa-pencil"
			class="pl-4"
			theme="outline"
			size="small"
			@click="open = true">
		</app-button>

		<app-button
			v-else
			icon="fa-plus"
			class="pl-4"
			theme="outline"
			size="small"
			@click="open = true">
		</app-button>

		<app-button
			v-if="product.id"
			icon="trash"
			class="pl-4"
			theme="danger"
			size="small"
			@click="showDeleteConfirm = true">
		</app-button>
	</div>

	<confirm-dialog
		:open="showDeleteConfirm"
		title="Delete Product"
		button="Delete"
		theme="danger"
		@confirmed="deleteProduct"
		@canceled="showDeleteConfirm = false">
		<p>
			Are you sure you want to delete <strong>{{ product.name }}</strong
			>? This action cannot be undone.
		</p>
	</confirm-dialog>

	<slideover title="Edit Product" :open="open" @close="close">
		<app-select
			label="Type"
			:model-value="values.type"
			:disabled="submitting"
			:options="productTypes"
			label-key="name"
			value-key="id"
			:error="errors['type']"
			@update:model-value="(value) => setFieldValue(`type`, value)">
		</app-select>

		<app-input
			label="Name"
			:model-value="values.name"
			:disabled="submitting"
			:error="errors['name']"
			@update:model-value="(value) => setFieldValue(`name`, value)">
		</app-input>

		<app-input
			label="Summary"
			type="textarea"
			:model-value="values.summary"
			:disabled="submitting"
			:error="errors['summary']"
			@update:model-value="(value) => setFieldValue(`summary`, value)">
		</app-input>

		<app-input
			label="Description"
			type="textarea"
			:model-value="values.description"
			:disabled="submitting"
			:error="errors['description']"
			@update:model-value="(value) => setFieldValue(`description`, value)">
		</app-input>

		<app-input
			label="Features & Benefits"
			type="textarea"
			:model-value="values.features_benefits"
			:disabled="submitting"
			:error="errors['features_benefits']"
			@update:model-value="(value) => setFieldValue(`features_benefits`, value)">
		</app-input>

		<div class="mt-4">
			<label class="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
			<div v-if="imagePreview" class="mb-3">
				<img :src="imagePreview" class="w-full max-h-48 object-contain rounded border border-gray-200" alt="Product image" />
				<button
					type="button"
					class="mt-1 text-xs text-red-600 hover:text-red-800"
					:disabled="submitting"
					@click="removeImage">
					Remove image
				</button>
			</div>
			<div class="flex items-center gap-3">
				<app-file-button
					name="product-image"
					accept="image/*"
					:disabled="submitting || imageUploading"
					@file-change="uploadImage">
					{{ imageUploading ? 'Uploading...' : (values.image ? 'Change Image' : 'Upload Image') }}
				</app-file-button>
				<span v-if="imageUploading" class="text-sm text-gray-400 animate-pulse">Uploading...</span>
			</div>
		</div>

		<template #action>
			<div class="flex justify-end w-full">
				<app-button @click="submit">Save</app-button>
			</div>
		</template>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch } from 'vue'
	import { useForm } from 'vee-validate'
	import * as Yup from 'yup'
	import ProductTypeService from '@/services/product-type'
	import type { ProductType } from '@/data/schema/product-type'
	import ProductService from '@/services/product'
	import FileService from '@/services/file'
	import useToaster from '@/stores/toaster'
	import { Product } from '@/data/schema/product'
	import { useRouter } from 'vue-router'
	import ConfirmDialog from '@/components/partials/dialogs/Confirm.vue'
	import AppButton from '@/components/base/AppButton.vue'
	import AppFileButton from '@/components/base/forms/AppFileButton.vue'

	const submitting = ref<boolean>(false)
	const showDeleteConfirm = ref(false)
	const deleting = ref(false)
	const imageUploading = ref(false)
	const imagePreview = ref<string | null>(null)

	const props = withDefaults(
		defineProps<{
			product?: any | null
		}>(),
		{
			product: null
		}
	)

	const toaster = useToaster()
	const router = useRouter()

	const productTypeService = new ProductTypeService()
	const productTypes = ref<ProductType[]>(undefined)

	const productService = new ProductService()
	const fileService = new FileService()

	const open = ref(false)

	const { resetForm, values, errors, setFieldValue, handleSubmit } = useForm<Product>({
		initialValues: {
			type: props.product?.type?.id ?? null,
			name: props.product?.name ?? null,
			summary: props.product?.summary ?? null,
			description: props.product?.description ?? null,
			features_benefits: props.product?.features_benefits ?? null,
			image: props.product?.image?.id ?? null
		},
		validationSchema: Yup.object({
			type: Yup.mixed().required('Type is required'), // Backend uses 'id_or_object_exists' which implies required
			name: Yup.string()
				.min(5, 'Name must be at least 5 characters')
				.max(255, 'Name must be at most 255 characters')
				.required('Name is required'),
			summary: Yup.string()
				.nullable()
				.min(5, 'Summary must be at least 5 characters')
				.max(255, 'Summary must be at most 255 characters'),
			description: Yup.string()
				.nullable()
				.min(5, 'Description must be at least 5 characters')
				.max(65535, 'Description has reached maximum allowed limit (65535 characters)'),
			features_benefits: Yup.string()
				.nullable()
				.min(5, 'Features & Benefits must be at least 5 characters')
				.max(65535, 'Features & Benefits has reached maximum allowed limit (65535 characters)')
		})
	})

	const emit = defineEmits(['completed'])

	const getProductTypes = async () => {
		const res = await productTypeService.getAll()
		productTypes.value = res.data
	}

	watch(open, (isOpen) => {
		if (!productTypes.value) {
			getProductTypes()
		}
		if (isOpen) {
			imagePreview.value = props.product?.image?.download_url ?? null
		}
	})

	const submit = handleSubmit(
		(values) => {
			submitting.value = true

			if (props.product?.id) {
				productService
					.update(props.product.id, values, { expand: 'type' })
					.then((result) => {
						toaster.success({ text: values.name + ' updated' })
						close()
						emit('completed', result.data)
					})
					.finally(() => {
						submitting.value = false
					})
			} else {
				productService
					.create(values, { expand: 'type' })
					.then((result) => {
						toaster.success({ text: values.name + ' created' })
						close()
						emit('completed', result.data)
					})
					.finally(() => {
						submitting.value = false
					})
			}
		},
		(errors) => {
			console.error(errors)
		}
	)

	function close() {
		open.value = false
		resetForm()
		imagePreview.value = null
		imageUploading.value = false
	}

	const uploadImage = (files: any[]) => {
		const file = files[0]
		if (!file) return

		imagePreview.value = file.preview ?? null
		imageUploading.value = true

		fileService
			.upload(file)
			.then((res) => {
				setFieldValue('image', res.data.file.id)
				imagePreview.value = res.data.file.download_url
			})
			.catch(() => {
				toaster.error({ text: 'Failed to upload image' })
				imagePreview.value = props.product?.image?.download_url ?? null
				setFieldValue('image', props.product?.image?.id ?? null)
			})
			.finally(() => {
				imageUploading.value = false
			})
	}

	const removeImage = () => {
		setFieldValue('image', null)
		imagePreview.value = null
	}

	const deleteProduct = async () => {
		if (!props.product?.id) return

		deleting.value = true
		try {
			const category = props.product.type?.category
			await productService.delete(props.product.id)
			toaster.success({ text: props.product.name + ' deleted successfully' })
			showDeleteConfirm.value = false
			await router.push(`/product-data/product-categories/${category}`)
		} catch (error) {
			console.error('Error deleting product:', error)
			toaster.error({ text: 'Failed to delete product' })
		} finally {
			deleting.value = false
		}
	}
</script>
